import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { Link } from '@tanstack/react-router';
import {
  can,
  type FleetStop,
  type FleetUnplaced,
  type FleetVehicle,
} from '@servwell/shared';
import { StatusBadge, Notice } from '../ui/Badge';
import { SearchInput } from '../ui/SearchInput';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';
import { IconFleet } from '../icons';
import { ASSET_STATUS, display, windowLabel } from '../status';
import { timeInTz, dayLabel, addDaysISO } from '../../lib/format';
import { hoursAndMinutes } from './FleetOverview';
import { DispatchMap, type MapPin } from '../map/DispatchMap';
import { MAP_OVERLAY_Z_LG } from '../map/overlay';
import { pinToneFor } from '../map/visitPinTone';

/**
 * The day each van is about to drive (112, approved prototype D5): pick a van on the
 * left, see its stops on the right.
 *
 * ⚠️ What the approved prototype shows and this does NOT, with the reason in each case —
 * because a screen that draws a number it cannot source is worse than one that admits the
 * gap:
 *
 *   · **Distance ("38 mi planned").** `DayRoute` carries no distance at all, and that is
 *     deliberate: `DAY_FIELD_MASK` is `polyline + legs.duration`, the field mask is what
 *     selects the Routes API billing SKU, and 108 ships a mutation (M35) whose whole job
 *     is to fail if the mask ever starts paying for a field nothing reads. Adding distance
 *     is a billing decision, not a layout one.
 *   · **Drive time.** ~~One billed Google call per van, and `null` on every deployment
 *     without a Routes key.~~ **WIRED 2026-08-23 (108), and the objection is honoured
 *     rather than overruled.** The concern was never that the code should not exist — it
 *     was that a page must not bill Google per van to render N copies of "drive n/a". So
 *     the panel asks for a route ONLY when `routingOn` says this deployment has a key,
 *     which today it does not: `enabled: routingOn` means zero calls and zero cost, and
 *     the tile still shows booked hours under a name that says so. The moment a key is
 *     installed the same panel starts drawing real legs, with no further change.
 *   · **"Optimize stop order".** It needs `rankSlots` AND a write that reorders visits;
 *     that write is ticket 109, which is not built. A disabled button with no destination
 *     is clutter, not a promise.
 */
export function FleetRoutes({
  tz,
  date,
  onDateChange,
  vehicles,
  unassigned,
  geocodingOn,
  routingOn,
}: {
  tz: string;
  date: string;
  onDateChange: (next: string) => void;
  vehicles: FleetVehicle[];
  /**
   * Calls no van covers, in full rather than as a count.
   *
   * ⭐ This is the half `/map` used to carry and the reason retiring it (D5) is not a
   * regression. `/map` drew every scheduled visit in the shop and named the ones it could
   * not draw. A page keyed on vehicles shows only what a van is assigned to, so without
   * this section a call given to a vanless tech would be a number in a sentence at best,
   * and invisible at worst.
   */
  unassigned: { stops: FleetStop[]; unplaced: FleetUnplaced[] };
  /** False when no geocoding key is configured — an empty map then has a REASON. */
  geocodingOn: boolean;
  /**
   * False when no Routes key is configured — the panel then never ASKS for a route.
   *
   * ⚠️ Not "hide the result": do not ask. `fleet.dayRoute` costs a billed Google call, so
   * a panel that queried optimistically and hid the answer would spend money per van
   * selection to render nothing. This is the flag that keeps the call from being made.
   */
  routingOn: boolean;
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const q = search.trim().toLowerCase();
  const shown = q
    ? vehicles.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          (v.plate ?? '').toLowerCase().includes(q) ||
          v.drivers.some((d) => d.name.toLowerCase().includes(q)),
      )
    : vehicles;

  // The selection follows the FILTERED list: a van hidden by a search it does not match
  // must not stay open in the panel beside it.
  const selected = shown.find((v) => v.assetId === selectedId) ?? shown[0] ?? null;

  /*
   * The pins ARE the panel's numbering. `i + 1` over the same already-sorted `stops` array
   * the panel walks, so the two cannot disagree — a map numbered from its own sort would
   * be a second opinion about the order of somebody's day.
   */
  const pins = useMemo<MapPin[]>(
    () =>
      (selected?.stops ?? []).map((s, i) => ({
        id: s.visitId,
        latitude: s.latitude,
        longitude: s.longitude,
        order: i + 1,
        title: s.title,
        body: [s.customerName, s.address ?? s.propertyLabel].filter(Boolean).join(' · '),
        tone: pinToneFor(s.status),
      })),
    [selected],
  );

  return (
    <div className="space-y-4">
      <GeocodeNotice geocodingOn={geocodingOn} />

      {/*
       * ⭐ `lg:` is the breakpoint the prototype was measured at (D6): below it the three
       * regions STACK — list, map, panel — and above it the panel floats over the map's
       * right edge. `min-w-0` on the right column is what stops a wide map forcing the
       * page body to scroll horizontally, which is the rule A-1 made non-negotiable.
       */}
      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="space-y-3">
          <SearchInput
            placeholder="Search vehicle or tech…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search vehicle or tech"
          />
          {shown.length === 0 ? (
            <EmptyState
              icon={IconFleet}
              title={
                vehicles.length === 0 ? 'No vehicles yet' : 'No vehicle matches that'
              }
              description={
                vehicles.length === 0
                  ? 'Add a van on the register and its day appears here.'
                  : 'Try the unit name, the plate, or the tech who drives it.'
              }
            />
          ) : (
            /*
             * A LIST of buttons, not a div with a click handler: every van is reachable by
             * Tab and openable with Enter or Space for free, and `aria-current` tells a
             * screen reader which one the panel is describing.
             */
            <ul className="space-y-2">
              {shown.map((v) => (
                <li key={v.assetId}>
                  <VehicleButton
                    vehicle={v}
                    selected={selected?.assetId === v.assetId}
                    onSelect={() => setSelectedId(v.assetId)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/*
         * ⚠️ `lg:self-start` is what makes the floating panel line up with the MAP.
         *
         * A grid item stretches to the row's height by default, so with more vans in the
         * left column than the map is tall, this cell grew past the map — and the panel,
         * which is `inset-y-4` against THIS box, hung 8px below the map's bottom edge over
         * blank page. MEASURED at 1440: panel bottom 703 against a map ending at 695.
         * `self-start` collapses the cell to its own content, so the panel's positioning
         * context and the pane it floats over are the same rectangle.
         */}
        <div className="relative min-w-0 lg:self-start">
          <MapPane
            pins={pins}
            base={selected?.base ?? null}
            baseLabel={selected ? `${selected.name} — home base` : ''}
            geocodingOn={geocodingOn}
            unmapped={selected?.unplaced.length ?? 0}
          />
          {selected ? (
            <div
              /*
               * 🔴 `MAP_OVERLAY_Z_LG` (`lg:z-[1100]`) is what makes this VISIBLE, and its absence was invisible
               * to every check short of looking at the pixels.
               *
               * Leaflet gives its panes `z-index: 400` (markers 600, popups 700, controls
               * 1000). This panel was `z-index: auto`, so the map painted straight over it
               * — and because Leaflet's tile pane sets `pointer-events: none`, hit-testing
               * still fell THROUGH to the panel. `document.elementFromPoint()` at the
               * panel's heading returned that heading, so the DOM probe reported the panel
               * on top while the screenshot showed no panel at all. Hit-testing is not
               * paint order. 1100 clears every layer Leaflet uses; the zoom controls live
               * in the opposite corner, so nothing is covered that matters.
               */
              className={`mt-4 lg:absolute lg:inset-y-4 lg:right-4 ${MAP_OVERLAY_Z_LG} lg:mt-0 lg:w-[22rem] lg:overflow-y-auto lg:rounded-[var(--radius)] lg:shadow-lg`}
              // Labelled as its own region so a keyboard user arriving here from the
              // vehicle list is told what they landed in.
              role="region"
              aria-label={`${selected.name} — planned day`}
            >
              <VehiclePanel
                vehicle={selected}
                tz={tz}
                date={date}
                onDateChange={onDateChange}
                routingOn={routingOn}
              />
            </div>
          ) : null}
        </div>
      </div>

      <UnassignedSection unassigned={unassigned} tz={tz} />
    </div>
  );
}

/**
 * Everything scheduled today that no van covers — a call given to a tech who drives no
 * vehicle, or to nobody at all.
 *
 * ⭐ Carried over from `/map`, which this page retires (D5). A count in a sentence would
 * not have been enough: the reason a call is here is actionable (assign a van) and the
 * reason one of them cannot be drawn is a DIFFERENT actionable thing (fix the address).
 * Both are said out loud, the way `/map` said them.
 */
function UnassignedSection({
  unassigned,
  tz,
}: {
  unassigned: { stops: FleetStop[]; unplaced: FleetUnplaced[] };
  tz: string;
}) {
  const total = unassigned.stops.length + unassigned.unplaced.length;
  if (total === 0) return null;
  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface p-4">
      <h2 className="text-sm font-semibold">
        {total} {total === 1 ? 'call' : 'calls'} today
        {total === 1 ? ' is' : ' are'} not on any van
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        They are on the schedule — they just have no vehicle assigned, so they appear on
        no van&apos;s route above.
      </p>
      <ul className="mt-3 space-y-2 text-sm">
        {unassigned.stops.map((s) => (
          <li key={s.visitId} className="flex flex-wrap items-baseline gap-x-2">
            <span className="tabular-nums text-xs text-muted-foreground">
              {timeInTz(s.scheduledStartAt, tz)}
            </span>
            <Link
              to="/jobs/$jobId"
              params={{ jobId: s.jobId }}
              className="font-medium underline decoration-dotted"
            >
              {s.title}
            </Link>
            <span className="text-xs text-muted-foreground">{s.customerName}</span>
          </li>
        ))}
        {unassigned.unplaced.map((u) => (
          <li key={u.visitId} className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{u.customerName}</span>
            <StatusBadge tone="neutral">{u.why}</StatusBadge>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "Some addresses have never been looked up", and the button that does it.
 *
 * 🔴 GATED on `org:manage`, which `/map` did not do. `map.place` is
 * `requirePermission('org','manage')` — owner only — while `/map` itself admits owner AND
 * office, and rendered this button unconditionally. An office dispatcher clicking it got
 * `Missing permission org:manage`: a control that looks live, fails, and teaches the user
 * that the product is broken. Running the lookup spends the shop's Google quota, which is
 * an owner's call, so the gate is right and the BUTTON was wrong. Retiring `/map` into
 * this page fixes it rather than copying it.
 */
function GeocodeNotice({ geocodingOn }: { geocodingOn: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const mayPlace = can(role, 'org', 'manage');
  const pending = useQuery(trpc.map.pending.queryOptions());
  const place = useMutation(
    trpc.map.place.mutationOptions({ onSuccess: () => void qc.invalidateQueries() }),
  );

  if (!geocodingOn) {
    return (
      <Notice tone="warn">
        Address lookup isn&apos;t switched on for this account, so nothing can be placed
        on the map yet.
      </Notice>
    );
  }
  const n = pending.data ?? 0;
  if (n === 0)
    return place.isError ? <Notice tone="danger">{place.error.message}</Notice> : null;

  return (
    <>
      <Notice tone="neutral">
        {n} {n === 1 ? 'address has' : 'addresses have'} not been looked up yet.
        They&apos;re done overnight
        {mayPlace ? (
          <>
            , or you can do it now.{' '}
            <Button
              variant="ghost"
              disabled={place.isPending}
              onClick={() => place.mutate({})}
            >
              {place.isPending ? 'Looking them up…' : 'Look them up now'}
            </Button>
          </>
        ) : (
          '. An owner can run the lookup sooner.'
        )}
      </Notice>
      {place.isError ? <Notice tone="danger">{place.error.message}</Notice> : null}
    </>
  );
}

/** How a van reads in the list: what it is, who has it, and whether it has a day. */
function VehicleButton({
  vehicle,
  selected,
  onSelect,
}: {
  vehicle: FleetVehicle;
  selected: boolean;
  onSelect: () => void;
}) {
  const stops = vehicle.stops.length;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={`w-full rounded-[var(--radius)] border px-3 py-2 text-left focus-visible:ring-2 focus-visible:ring-ring/50 ${
        selected
          ? 'border-brand bg-[var(--sw-brand-soft,transparent)]'
          : 'border-border bg-surface'
      }`}
    >
      <p className="truncate font-medium">{vehicle.name}</p>
      <p className="truncate text-xs text-muted-foreground">
        {[vehicle.plate, vehicle.drivers.map((d) => d.name).join(', ') || 'Unassigned']
          .filter(Boolean)
          .join(' · ')}
      </p>
      {/*
       * ⭐ "No route planned" is a STATE, not an absence. The acceptance criterion for
       * this ticket is that a van with no assignment and a van with no geocode both render
       * honestly; a row that simply stopped after the plate would leave the reader to
       * guess which of "nothing booked" and "failed to load" they were looking at.
       */}
      <p
        className={`text-xs ${stops > 0 ? 'text-[var(--sw-brand-text)]' : 'text-muted-foreground'}`}
      >
        {stops === 0
          ? 'No route planned'
          : `${stops} ${stops === 1 ? 'stop' : 'stops'}${
              vehicle.unplaced.length > 0 ? ` · ${vehicle.unplaced.length} unmapped` : ''
            }`}
      </p>
    </button>
  );
}

/** The floating panel: one van's day, in order. */
function VehiclePanel({
  vehicle,
  tz,
  date,
  onDateChange,
  routingOn,
}: {
  vehicle: FleetVehicle;
  tz: string;
  date: string;
  onDateChange: (next: string) => void;
  routingOn: boolean;
}) {
  const trpc = useTRPC();
  /*
   * The routing engine's call site (108, wired 2026-08-23).
   *
   * ⭐ `enabled` carries the whole cost argument. One selected van, one billed Google
   * call — and on a deployment with no Routes key, NO call at all, because the answer is
   * known in advance and is not worth paying for. That is what 112 objected to, and it
   * is honoured here rather than argued with.
   *
   * ⚠️ Deliberately NOT part of `fleet.day`: folding it in would bill once per van on
   * every page load, including the loads where nobody opens a panel.
   */
  const routeQuery = useQuery({
    ...trpc.fleet.dayRoute.queryOptions({ assetId: vehicle.assetId, date }),
    enabled: routingOn,
  });
  const route = routeQuery.data;
  const status = display(ASSET_STATUS, vehicle.status);
  /*
   * ⚠️ Both numbers come from the SERVER. The first version of this panel subtracted the
   * two timestamps here, which is zone-free arithmetic and looked harmless — and it put
   * `new Date(` into `v2/fleet`, which is swept for exactly that (107). The sweep is not
   * about this subtraction; it is about there being no such thing as a small exception to
   * where date arithmetic lives.
   */
  const booked = vehicle.bookedMinutes;
  const openEnded = vehicle.stopsWithoutEnd;

  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface">
      <header className="space-y-1 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="font-heading text-sm font-semibold">{vehicle.name}</h2>
          <StatusBadge tone={vehicle.stops.length > 0 ? 'success' : 'neutral'}>
            {vehicle.stops.length > 0 ? 'Route planned' : 'No route planned'}
          </StatusBadge>
        </div>
        <p className="text-xs text-muted-foreground">
          {[vehicle.plate, vehicle.drivers.map((d) => d.name).join(', ') || 'Unassigned']
            .filter(Boolean)
            .join(' · ')}
        </p>
        {/* The van's own status is a separate fact from whether it has a day — a van in
            the shop can still have calls booked on it, and that is worth seeing. */}
        {vehicle.status !== 'active' ? (
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        ) : null}
      </header>

      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <Button
          variant="ghost"
          className="px-2 py-1"
          aria-label="Previous day"
          onClick={() => onDateChange(addDaysISO(date, -1))}
        >
          ‹
        </Button>
        <span className="text-sm font-medium">{dayLabel(date, tz)}</span>
        <Button
          variant="ghost"
          className="px-2 py-1"
          aria-label="Next day"
          onClick={() => onDateChange(addDaysISO(date, 1))}
        >
          ›
        </Button>
      </div>

      {/*
       * ⚠️ Two tiles without routing, three with it — and "Booked" does NOT move over to
       * make room for a number that may not arrive. `routingOn` is known before the query
       * is made, so the grid's shape is decided by whether this deployment HAS routing,
       * never by whether one request happened to answer. A tile that appears and vanishes
       * on refetch is a layout that shifts under the pointer.
       */}
      <dl
        className={`grid divide-x divide-border border-b border-border text-center ${
          routingOn ? 'grid-cols-3' : 'grid-cols-2'
        }`}
      >
        <div className="px-3 py-2">
          <dt className="text-xs text-muted-foreground">Stops</dt>
          <dd className="font-heading text-lg font-bold tabular-nums">
            {vehicle.stops.length}
          </dd>
        </div>
        <div className="px-3 py-2">
          <dt className="text-xs text-muted-foreground">Booked</dt>
          <dd className="font-heading text-lg font-bold tabular-nums">
            {hoursAndMinutes(booked)}
          </dd>
        </div>
        {routingOn ? (
          <div className="px-3 py-2">
            <dt className="text-xs text-muted-foreground">Driving</dt>
            <dd className="font-heading text-lg font-bold tabular-nums">
              {/*
               * Three states, not two. Pending is not the same as unanswerable, and
               * neither is a number — an em dash for "we do not know yet", "n/a" for
               * "Google could not say". Never 0m, which would read as a day with no
               * driving in it.
               */}
              {routeQuery.isPending
                ? '—'
                : route && route.totalMinutes !== null
                  ? hoursAndMinutes(route.totalMinutes)
                  : 'n/a'}
            </dd>
          </div>
        ) : null}
      </dl>
      {/*
       * WHY the drive figures are missing, once rather than on every leg. `DayRoute.reason`
       * is the engine's own words — "Drive times are not switched on for this deployment",
       * "we could not reach Google" — and repeating it beside each of eight legs would
       * bury the stops it is meant to annotate.
       */}
      {routingOn && route?.degraded && route.reason ? (
        <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
          {route.reason}
        </p>
      ) : null}
      {openEnded > 0 ? (
        <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
          {openEnded === 1
            ? '1 stop has no end time and adds nothing to the booked total.'
            : `${openEnded} stops have no end time and add nothing to the booked total.`}
        </p>
      ) : null}

      {vehicle.stops.length === 0 && vehicle.unplaced.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          Nothing booked for this van on this day.
        </p>
      ) : (
        <ol>
          {vehicle.stops.map((s, i) => (
            <Fragment key={s.visitId}>
              {/*
               * ⭐ The drive from the PREVIOUS stop to this one, between the two rows it
               * joins — `legMinutes[i - 1]`, because leg 0 is stop 0 → stop 1.
               *
               * ⚠️ A null leg says "drive n/a", NEVER "0 min". `engine.ts` makes that a
               * hard rule and the reason is that a zero sorts first and reads as "no
               * distance": a confident lie about two places twenty minutes apart. The
               * whole point of `DriveTime` having no number on its failure branch is that
               * this renderer cannot invent one.
               *
               * ⚠️ The test is `typeof === 'number'`, NOT `=== null`. The server promises
               * one entry per leg and a test holds it to that, but an array that is ever
               * SHORT yields `undefined` here — and `undefined === null` is false, so the
               * null-check version would render the string "undefined min drive". Ask what
               * a number IS rather than enumerating the ways it might be missing.
               */}
              {i > 0 && route && !routeQuery.isPending ? (
                <li aria-hidden className="px-4">
                  <span className="ml-3 block border-l border-dashed border-border py-1 pl-4 text-xs text-muted-foreground">
                    {typeof route.legMinutes[i - 1] === 'number'
                      ? `${route.legMinutes[i - 1]} min drive`
                      : 'drive n/a'}
                  </span>
                </li>
              ) : null}
              <li className="flex gap-3 border-b border-border px-4 py-3 last:border-b-0">
                {/* The number IS the map pin. One ordering, drawn twice. */}
                <span
                  aria-hidden
                  className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white"
                >
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-xs tabular-nums text-muted-foreground">
                    <span className="sr-only">{`Stop ${i + 1}, `}</span>
                    {timeInTz(s.scheduledStartAt, tz)}
                    {s.scheduledEndAt ? ` – ${timeInTz(s.scheduledEndAt, tz)}` : ''}
                    {/* An arrival WINDOW is not a promise of a time, so it is named.
                        `windowLabel` is null for `exact` — where the promise IS the time
                        printed just above — so the null check replaces the `!== 'exact'`
                        this line used to carry, and the two readers of this vocabulary
                        can no longer disagree about which values get a word. */}
                    {windowLabel(s.window) ? ` · ${windowLabel(s.window)}` : ''}
                  </p>
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: s.jobId }}
                    className="block truncate font-medium underline decoration-dotted"
                  >
                    {s.title}
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">
                    {[s.customerName, s.address ?? s.propertyLabel]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </li>
            </Fragment>
          ))}
        </ol>
      )}

      {/*
       * ⭐ Calls this van is driving to that the map cannot draw. Listed, never dropped:
       * a panel showing four stops for a tech doing five is how a dispatcher reads a light
       * afternoon and books on top of a job nobody could see.
       */}
      {vehicle.unplaced.length > 0 ? (
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs font-medium">Not on the map</p>
          <ul className="mt-1 space-y-1">
            {vehicle.unplaced.map((u) => (
              <li key={u.visitId} className="text-xs text-muted-foreground">
                <span className="font-medium">{u.customerName}</span> — {u.why}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="border-t border-border px-4 py-3">
        <Link to="/schedule" className="text-sm underline decoration-dotted">
          Open in Schedule
        </Link>
      </div>
    </section>
  );
}

/**
 * The map, or the reason there isn't one.
 *
 * ⭐ Four states, not two, because "an empty grey rectangle" is the same lie in three
 * different situations. A reader who sees nothing must be able to tell "this van has no
 * calls" from "this van's calls have no coordinates" from "nobody is looking addresses up
 * on this deployment at all" — the middle one is recoverable by fixing an address, the
 * last one is a deployment setting, and the first one is simply a quiet day.
 */
function MapPane({
  pins,
  base,
  baseLabel,
  geocodingOn,
  unmapped,
}: {
  pins: MapPin[];
  base: { latitude: number; longitude: number } | null;
  baseLabel: string;
  geocodingOn: boolean;
  unmapped: number;
}) {
  if (pins.length > 0 || base) {
    return (
      <div className="space-y-2">
        <DispatchMap
          pins={pins}
          base={base ? { ...base, label: baseLabel } : null}
          showOrderPath
          className="h-[24rem] w-full rounded-[var(--radius)] border border-border lg:h-[32rem]"
        />
        {/*
         * The dashes say it and so does this line. A line between two pins is the thing
         * every mapping product uses to mean "this is the way you drive", and this one is
         * not that — it is the order of the day with the roads left out. Saying so costs
         * one sentence; not saying so invites a dispatcher to read distance off it.
         */}
        {pins.length > 1 ? (
          <p className="text-xs text-muted-foreground">
            The dashed line is the order of the stops, not the roads driven. Drive times
            and distances need a Google Routes key, which this deployment does not have.
          </p>
        ) : null}
      </div>
    );
  }

  const why = !geocodingOn
    ? 'Addresses are not being looked up on this deployment, so there are no coordinates to draw.'
    : unmapped > 0
      ? `This van's ${unmapped === 1 ? 'call has' : 'calls have'} no coordinates yet — the reasons are listed in the panel.`
      : 'Nothing to draw for this van on this day.';

  return (
    <div className="flex h-[24rem] items-center justify-center rounded-[var(--radius)] border border-dashed border-border bg-muted/30 px-6 text-center lg:h-[32rem] lg:pr-[24rem]">
      <p className="max-w-sm text-sm text-muted-foreground">
        {why} Every stop is still listed in order beside this.
      </p>
    </div>
  );
}
